import {
  type BaseFile,
  API_ACCOUNT_EXECUTIVES,
  API_ACCOUNT_EXECUTIVES_BY_ID,
  API_ADMIN_ALTERNATIVE_NAMES,
  API_ADMIN_CONFIG_BY_TYPE,
  API_ADMIN_CREDENTIAL_TYPES,
  API_ADMIN_CREDENTIALS,
  API_ADMIN_GET_SETTING,
  API_ADMIN_UPDATE_SETTING,
  API_API_KEY_BY_ID,
  API_API_KEY_REVOKE,
  API_API_KEY_SCOPES,
  API_API_KEYS,
  API_BANKER_DATA,
  API_BANKER_DELETE_DATA,
  API_BANKER_UPDATE_DATA,
  API_BUDGET_TEMPLATE_LOGS,
  API_CONFIG_FIELD_LOG,
  API_CONVERT_LOAN,
  API_EMAIL_BROADCAST,
  API_EMAIL_LOG_DETAILS,
  API_EMAIL_LOGS,
  API_FAQ,
  API_FAQ_BY_ID,
  API_FAQ_EXCHANGE_ORDER,
  API_GET_NAVBAR_DATA,
  API_INELIGIBLE_BANKERS,
  API_INTEGRATION_CHANNEL_BY_ID,
  API_INTEGRATION_CHANNEL_TEST,
  API_INTEGRATION_CHANNELS,
  API_LOAN_DELETE_ALL,
  API_LOAN_MAIL,
  API_LOAN_MAIL_BY_ID,
  API_LOAN_MAIL_DOWNLOAD,
  API_LOAN_MAIL_LOG,
  API_LOAN_SCENARIO,
  API_LOAN_SHARE_SCENARIO,
  API_LOAN_SHARE_SCENARIO_GET,
  API_LOAN_TERM_SHEET,
  API_LOAN_TERM_SHEET_ADD_FIELD,
  API_LOAN_TERM_SHEET_BY_ID,
  API_LOAN_TERM_SHEET_LIST,
  API_LOGIN_PAGE_MODAL,
  API_PERMISSION_DESCRIPTION,
  API_PERMISSION_DETAILS,
  API_PERMISSION_LOGS,
  API_REMOVE_LOAN_TERM_SHEET,
  API_SEND_BROKER_VERIFICATION_MESSAGE,
  API_SEND_BROKER_VERIFICATION_PDF,
  API_SEND_BROKER_VERIFICATION_REQUEST,
  API_SEND_SIGN_BROKER_APPROVAL,
  API_SERVER_LOG,
  API_SYNC_LOAN_TERM_SHEET,
  API_UPDATE_BROKER_VERIFICATION_DOCUMENT,
  API_UPDATE_LOAN_TERM_SHEET,
  API_USER_COUNTERPARTY_APPROVAL_NOTIFY,
  API_USER_PAGE_PERMISSION_OVERRIDES,
  API_USER_PERMISSION_OVERRIDES,
} from 'config'
import { AccountVerificationType, BrokerIdentifyCategory } from 'pages/Admin'
import type { BusinessHour } from 'pages/Admin/AdminTools/Configuration/SecondaryMarketing'
import type { BrokerVerificationDocument } from 'pages/ManageAccounts'
import Api from 'services/api'
import { formatDateYMD } from 'utils'

import { downloadFile, fileJsonConvert } from '../documents'

export const AccountVerificationSettingKey = {
  [AccountVerificationType.Broker]: 'BROKER_VERIFICATION',
  [AccountVerificationType.Correspondent]: 'CORRESPONDENT_VERIFICATION',
  [AccountVerificationType.Tablefunder]: 'TABLEFUNDER_VERIFICATION',
  [AccountVerificationType.ContractorApproval]: 'CONTRACTOR_APPROVAL',
}

export const getPermissionDetails = () => {
  return Api.get(API_PERMISSION_DETAILS)
}

export const updatePermissions = (data: any) => {
  return Api.post(API_PERMISSION_DETAILS, data)
}

export const getUserPermissionOverrides = (userId: number) => {
  return Api.get(API_USER_PERMISSION_OVERRIDES, {}, { userId })
}

export const updateUserPermissionOverrides = (userId: number, grants: number[], denies: number[]) => {
  return Api.put(API_USER_PERMISSION_OVERRIDES, { grants, denies }, { userId })
}

export const getUserPagePermissionOverrides = (userId: number) => {
  return Api.get(API_USER_PAGE_PERMISSION_OVERRIDES, {}, { userId })
}

export const updateUserPagePermissionOverrides = (userId: number, denies: string[]) => {
  return Api.put(API_USER_PAGE_PERMISSION_OVERRIDES, { denies }, { userId })
}

export const getPermissionLogs = (permission: string) => {
  return Api.get(API_PERMISSION_LOGS, {}, { permission })
}

export const getConfigFieldLogs = (fieldKey: string) => {
  return Api.get(API_CONFIG_FIELD_LOG, { fieldKey })
}

export const updatePermissionDescription = (id: number, description: string, tooltip: string) => {
  return Api.patch(API_PERMISSION_DESCRIPTION, { description, tooltip }, { id })
}

export const getBudgetTemplateLogs = (key: string) => {
  return Api.get(API_BUDGET_TEMPLATE_LOGS, {}, { key })
}

export const convertLoan = (loanNumber: number, type: string) => {
  if (type === 'Delete') return Api.delete(API_LOAN_DELETE_ALL, {}, { loanNumber })
  return Api.post(API_CONVERT_LOAN, { type }, { loanNumber })
}

export const getSetting = (key: string) => {
  return Api.get(API_ADMIN_GET_SETTING, {}, { key })
}

export const getNavBarData = () => {
  return Api.post(API_GET_NAVBAR_DATA, { now: formatDateYMD(new Date()) })
}

export const updateSetting = (key: string, value: string) => {
  return Api.put(API_ADMIN_UPDATE_SETTING, { key, value })
}

export const getBrokerVerificationCategories = async (
  type: AccountVerificationType,
): Promise<BrokerIdentifyCategory[]> => {
  const categories = await getSetting(AccountVerificationSettingKey[type])
  try {
    const values = categories || {}
    return JSON.parse(values.value) || []
  } catch {
    return []
  }
}

export const setBusinessHours = (value: BusinessHour) => {
  return Api.put(API_ADMIN_UPDATE_SETTING, { key: 'BUSINESS_HOURS', value: JSON.stringify(value) })
}

export const getBusinessHours = async (): Promise<BusinessHour | null> => {
  const buzHours = await getSetting('BUSINESS_HOURS')
  try {
    const values = buzHours || {}
    return JSON.parse(values.value) || null
  } catch {
    return null
  }
}

export const setBrokerVerificationCategories = async (
  values: BrokerIdentifyCategory[],
  type: AccountVerificationType,
) => {
  return updateSetting(AccountVerificationSettingKey[type], JSON.stringify(values))
}

export const sendBrokerVerificationRequest = (userId: number) => {
  return Api.post(API_SEND_BROKER_VERIFICATION_REQUEST, {}, { userId })
}

export const getCounterpartyApprovalNotify = (userId: number) => {
  return Api.get(API_USER_COUNTERPARTY_APPROVAL_NOTIFY, {}, { userId })
}

export const updateCounterpartyApprovalNotify = (userId: number, data: Record<string, any>) => {
  return Api.put(API_USER_COUNTERPARTY_APPROVAL_NOTIFY, data, { userId })
}

export const updateBrokerVerificationDocument = (userId: number, doc: BrokerVerificationDocument[]) => {
  return Api.post(API_UPDATE_BROKER_VERIFICATION_DOCUMENT, doc, { userId })
}

export const deleteBrokerVerificationDocument = (userId: number, docIds: number[]) => {
  return Api.delete(API_UPDATE_BROKER_VERIFICATION_DOCUMENT, docIds, { userId })
}

export const sendBrokerVerificationMessage = (userId: number, data: any) => {
  return Api.post(API_SEND_BROKER_VERIFICATION_MESSAGE, data, { userId })
}

export const sendBrokerVerificationPdf = (userId: number) => {
  return Api.post(API_SEND_BROKER_VERIFICATION_PDF, {}, { userId }, { timeout: 150000 })
}

export const sendSignBrokerApprovalRequest = (
  userId: number,
  data: { signorId?: number; cc?: string[]; bcc?: string[] },
) => {
  return Api.post(API_SEND_SIGN_BROKER_APPROVAL, data, { userId })
}

export const getBroadcastEmails = (filters: Record<string, any>) => {
  return Api.get(API_EMAIL_BROADCAST, filters)
}

export const createBroadcastEmail = (data: Record<string, string | File>) => {
  const json = fileJsonConvert(data)
  return Api.uploadFiles(API_EMAIL_BROADCAST, data, {}, json.files, { timeout: 300000 })
}

export const getBankerData = (data: Record<string, any>) => {
  return Api.get(API_BANKER_DATA, data)
}

export const updateBankerData = (data: Record<string, any>) => {
  return Api.post(API_BANKER_UPDATE_DATA, data)
}

export const deleteBankerData = (data: Record<string, any>) => {
  return Api.post(API_BANKER_DELETE_DATA, data)
}

export const getIneligibleBankers = () => {
  return Api.get(API_INELIGIBLE_BANKERS)
}

export const logInPageModalAPI = (data: Record<string, any>) => {
  return Api.post(API_LOGIN_PAGE_MODAL, data)
}

export const filterAccountExecutives = () => {
  return Api.get(API_ACCOUNT_EXECUTIVES)
}

export const submitAccountExecutive = (data: Record<string, any>) => {
  return Api.uploadFiles(API_ACCOUNT_EXECUTIVES, data, {}, [])
}

export const updateAccountExecutive = (id: number, data: Record<string, any>) => {
  return Api.uploadFiles(API_ACCOUNT_EXECUTIVES_BY_ID, data, { id }, [])
}

export const updateAccountExecutiveProps = (id: number, data: Record<string, any>) => {
  return Api.put(API_ACCOUNT_EXECUTIVES_BY_ID, data, { id })
}

export const deleteAccountExecutive = (id: number) => {
  return Api.delete(API_ACCOUNT_EXECUTIVES_BY_ID, {}, { id })
}

export const getFaq = () => {
  return Api.get(API_FAQ)
}

export const addFaq = (data: Record<string, string>) => {
  return Api.post(API_FAQ, data)
}

export const updateFaq = (id: number, data: Record<string, string>) => {
  return Api.put(API_FAQ_BY_ID, data, { id })
}

export const deleteFaq = (id: number) => {
  return Api.delete(API_FAQ_BY_ID, {}, { id })
}

export const exchangeFaqOrder = (id1: number, id2: number) => {
  return Api.post(API_FAQ_EXCHANGE_ORDER, {}, { id1, id2 })
}

export const getLoanTermSheetTemp = (
  productType: string,
  transactionType: string,
  type: string,
  sheetType: string = 'termSheet',
) => {
  return Api.get(API_LOAN_TERM_SHEET, { sheetType }, { productType, transactionType, type })
}

export const getAllLoanTermSheetTemps = () => {
  return Api.get(API_LOAN_TERM_SHEET_LIST)
}

export const getLoanTermSheetTempById = (id: number) => {
  return Api.get(API_LOAN_TERM_SHEET_BY_ID, {}, { id })
}

export const createLoanTermSheetTemp = (data: any) => {
  return Api.post(API_LOAN_TERM_SHEET_LIST, data)
}

export const deleteLoanTermSheetTemp = (id: number) => {
  return Api.delete(API_LOAN_TERM_SHEET_BY_ID, {}, { id })
}

export const loanStructureShareScenario = (data: any) => {
  return Api.post(API_LOAN_SHARE_SCENARIO, data)
}

export const onShareScenario = (data: any) => {
  return Api.put(API_LOAN_SHARE_SCENARIO, data)
}

export const getLoanScenario = (token: string, id: string) => {
  return Api.get(API_LOAN_SHARE_SCENARIO_GET, {}, { token, id })
}

export const filterLoanScenarios = (filters: Record<string, any>) => {
  return Api.get(API_LOAN_SCENARIO, filters)
}

export const updateLoanTermSheetTemp = (id: number, data: any) => {
  return Api.put(API_UPDATE_LOAN_TERM_SHEET, data, { id })
}

export const syncLoanTermSheetTemp = (id: number, data: any) => {
  return Api.put(API_SYNC_LOAN_TERM_SHEET, data, { id })
}

export const addLoanTermSheetField = (id: number, data: any) => {
  return Api.post(API_LOAN_TERM_SHEET_ADD_FIELD, data, { id })
}

export const removeLoanTermSheetField = (id: number, index: number) => {
  return Api.delete(API_REMOVE_LOAN_TERM_SHEET, {}, { id, index })
}

export const getAlternativeNames = (cond: { firstName: string; middleName: string; lastName: string }) => {
  return Api.get(API_ADMIN_ALTERNATIVE_NAMES, cond)
}

export const updateAlternativeNames = (
  cond: { firstName: string; middleName: string; lastName: string },
  data: Record<string, any>[],
) => {
  return Api.put(API_ADMIN_ALTERNATIVE_NAMES, {
    ...cond,
    data,
  })
}

/**
 * Admin config, cached briefly.
 *
 * Coalescing in Api.get only helps callers that overlap. These do not always overlap: five
 * components fetch `features` independently (ConfigurablePage, SharedNexysLoan, LoanOverview,
 * Vendors/NexysConnections, the Features admin page) and re-fetch as they mount and unmount while
 * a user moves around a screen. Measured 2026-09-21: `features` and `permissions` each went out
 * 25 times in a few minutes on one tenant.
 *
 * A short TTL is safe here in a way it would not be for, say, an unread count: these are
 * configuration values an administrator changes occasionally, not per-request state. Thirty
 * seconds collapses a screen's worth of duplicate fetches while still picking up a change well
 * inside the time it takes to navigate to the screen that shows it.
 *
 * setAdminConfig clears the entry it wrote, so an admin saving a value sees their own change
 * immediately rather than up to thirty seconds later.
 */
const adminConfigCache = new Map<string, { at: number; value: any }>()
const ADMIN_CONFIG_TTL_MS = 30_000

export const clearAdminConfigCache = (type?: string) => {
  if (type === undefined) adminConfigCache.clear()
  else
    for (const key of Array.from(adminConfigCache.keys())) {
      if (key.startsWith(`${type}|`)) adminConfigCache.delete(key)
    }
}

export const getAdminConfig = (type: string, props: Record<string, any> = {}) => {
  let key: string | null = null
  try {
    key = `${type}|${JSON.stringify(props)}`
  } catch {
    key = null
  }
  if (key !== null) {
    const hit = adminConfigCache.get(key)
    if (hit && Date.now() - hit.at < ADMIN_CONFIG_TTL_MS) return Promise.resolve(hit.value)
  }
  return Api.get(API_ADMIN_CONFIG_BY_TYPE, props, { type }).then((value: any) => {
    // Only a resolved value is cached; a rejection propagates and leaves the cache untouched, so
    // a failed fetch is retried rather than remembered.
    if (key !== null) adminConfigCache.set(key, { at: Date.now(), value })
    return value
  })
}

export const getWhiteLabelByHost = (host: string): Promise<{ logoUrl: string; footer: Record<string, any> } | null> => {
  return Api.get(API_ADMIN_CONFIG_BY_TYPE, { host }, { type: 'whiteLabelByHost' })
}

export const setAdminConfig = (type: string, values: Record<string, any>) => {
  // Drop the cached copy for this type before the write, not after: the request may resolve after
  // some other component has already re-read it, and a stale entry written back in between would
  // outlive the change the admin just made.
  clearAdminConfigCache(type)
  return Api.post(API_ADMIN_CONFIG_BY_TYPE, values, { type }).then((res: any) => {
    clearAdminConfigCache(type)
    return res
  })
}

export const copyAccountTypeProfile = (sourceKey: string, title: string) => {
  return Api.post(API_ADMIN_CONFIG_BY_TYPE, { sourceKey, title }, { type: 'accountTypes/copy' })
}

export const getAccountTypeUsage = (): Promise<Record<string, number>> => {
  return Api.get(API_ADMIN_CONFIG_BY_TYPE, {}, { type: 'accountTypes/usage' })
}

export const deleteAccountTypeProfile = (key: string) => {
  return Api.delete(API_ADMIN_CONFIG_BY_TYPE, {}, { type: `accountTypes/${key}` })
}

export interface IBudgetTemplateSection {
  label: string
  value: string
  defaultCostType: string
  items: string[]
}

export interface IBudgetTemplateConfig {
  title: string
  order: number
  isDefault: boolean
  sections: IBudgetTemplateSection[]
  itemMapping: Record<string, string>
}

export type IBudgetTemplatesConfig = Record<string, IBudgetTemplateConfig>

export const getBudgetTemplatesConfig = (): Promise<IBudgetTemplatesConfig> => {
  return Api.get(API_ADMIN_CONFIG_BY_TYPE, {}, { type: 'budgetTemplates' })
}

export const setBudgetTemplatesConfig = (values: IBudgetTemplatesConfig) => {
  return Api.post(API_ADMIN_CONFIG_BY_TYPE, values, { type: 'budgetTemplates' })
}

export const addBudgetTemplateConfig = (
  title: string,
  sourceKey?: string,
): Promise<{ key: string; templates: IBudgetTemplatesConfig }> => {
  return Api.post(API_ADMIN_CONFIG_BY_TYPE, { title, sourceKey }, { type: 'budgetTemplates/add' })
}

export const setDefaultBudgetTemplateConfig = (key: string): Promise<IBudgetTemplatesConfig> => {
  return Api.post(API_ADMIN_CONFIG_BY_TYPE, {}, { type: `budgetTemplates/default/${key}` })
}

export const deleteBudgetTemplateConfig = (key: string): Promise<IBudgetTemplatesConfig> => {
  return Api.delete(API_ADMIN_CONFIG_BY_TYPE, {}, { type: `budgetTemplates/${key}` })
}

export interface IBudgetTemplateAiImportResult {
  sections: IBudgetTemplateSection[]
  itemMapping: Record<string, string>
  /** Rows the model returned that carried no usable label or title. */
  skipped: number
  /** Which path produced this: the AI, or the sheet's own structure when the AI was unavailable. */
  source?: 'ai' | 'spreadsheet'
}

/**
 * Derive a budget template's sections and line items from a spreadsheet. Returns a preview only —
 * the caller names it and saves it with `setBudgetTemplatesConfig`.
 */
export const aiImportBudgetTemplate = (
  file: File,
): Promise<{ success: boolean; message?: string; data?: IBudgetTemplateAiImportResult }> => {
  return Api.uploadFiles(API_ADMIN_CONFIG_BY_TYPE, {}, { type: 'budgetTemplates/aiImport' }, [file], {
    timeout: 300000,
  })
}

export const getEmailPreview = (emailKey: string): Promise<{ subject: string; html: string }> => {
  return Api.get(`${API_ADMIN_CONFIG_BY_TYPE}/preview/${emailKey}`, {}, { type: 'emailHardcoded' })
}

export interface ICustomerEmail {
  title: string
  description: string
  note: string
  subject: string
}

export const getEmailHardcodedDetail = (
  emailKey: string,
): Promise<{
  subject: string
  elements: any[]
  customerEmail: ICustomerEmail | null
  originalPlaceholders: string[]
  files: BaseFile[]
  emailTo?: string[]
  emailCc?: string[]
  emailBcc?: string[]
  emailExclude?: string[]
  supportsRecipients?: boolean
  supportsCcBcc?: boolean
}> => {
  return Api.get(`${API_ADMIN_CONFIG_BY_TYPE}/detail/${emailKey}`, {}, { type: 'emailHardcoded' })
}

export const updateEmailHardcoded = (
  emailKey: string,
  data: {
    subject: string
    elements?: any[]
    customerEmail?: ICustomerEmail | null
    files?: BaseFile[]
    emailTo?: string[]
    emailCc?: string[]
    emailBcc?: string[]
    emailExclude?: string[]
  },
): Promise<any> => {
  return Api.put(`${API_ADMIN_CONFIG_BY_TYPE}/detail/${emailKey}`, data, { type: 'emailHardcoded' })
}

export const getCredentials = (filterQuery: Record<string, any>) => {
  return Api.get(API_ADMIN_CREDENTIALS, filterQuery)
}

export const getCredentialTypes = () => {
  return Api.get(API_ADMIN_CREDENTIAL_TYPES)
}

export const getEmailDnsConfig = (dns: string) => {
  return Api.get(API_ADMIN_CONFIG_BY_TYPE, {}, { type: `emailDNSSetup/${dns}` })
}

export const deleteEmailDnsConfig = (dns: string) => {
  return Api.delete(API_ADMIN_CONFIG_BY_TYPE, {}, { type: `emailDNSSetup/${dns}` })
}

export const verifyEmailDnsConfig = (dns: string) => {
  return Api.patch(API_ADMIN_CONFIG_BY_TYPE, {}, { type: `emailDNSSetup/${dns}` })
}

export const createEmailDnsConfig = (data: Record<string, any>) => {
  return Api.put(API_ADMIN_CONFIG_BY_TYPE, data, { type: 'emailDNSSetup' })
}

export const sendEmailDnsConfigEmail = (dns: string, data: Record<string, any>) => {
  return Api.post(API_ADMIN_CONFIG_BY_TYPE, data, { type: `emailDNSSetup/${dns}` })
}

/**
 * Invoices
 */
export const getMails = (filters: Record<string, any>) => {
  return Api.get(API_LOAN_MAIL, filters)
}

export const addMail = (data: Record<string, any>) => {
  return Api.post(API_LOAN_MAIL, data)
}

export const updateMail = (id: number, data: Record<string, any>) => {
  return Api.put(API_LOAN_MAIL_BY_ID, data, { id })
}

export const deleteMail = (id: number) => {
  return Api.delete(API_LOAN_MAIL_BY_ID, {}, { id })
}

export const downloadMails = async (isLoan: boolean) => {
  const res: Blob = await Api.get(
    API_LOAN_MAIL_DOWNLOAD,
    { isLoan },
    {},
    {
      responseType: 'blob',
    },
  )
  downloadFile(`Mails.csv`, res)
}

export const getMailLogs = (id: number, key: string) => {
  return Api.get(API_LOAN_MAIL_LOG, {}, { id, key })
}

export const getEmailLogs = (filters: Record<string, any> = {}) => {
  return Api.get(API_EMAIL_LOGS, filters)
}

export const getEmailLogDetails = (id: number) => {
  return Api.get(API_EMAIL_LOG_DETAILS, {}, { id })
}

export const getServerLog = (filters: Record<string, any> = {}) => {
  return Api.get(API_SERVER_LOG, filters)
}

export const getApiKeys = () => {
  return Api.get(API_API_KEYS)
}

/** The scopes an admin may grant, served by the backend so the picker cannot drift from it. */
export const getApiKeyScopes = () => {
  return Api.get(API_API_KEY_SCOPES)
}

/** Returns `{ record, key }`. `key` is the only copy of the credential that will ever exist. */
export const createApiKey = (data: Record<string, any>) => {
  return Api.post(API_API_KEYS, data)
}

export const revokeApiKey = (id: number) => {
  return Api.post(API_API_KEY_REVOKE, {}, { id })
}

export const deleteApiKey = (id: number) => {
  return Api.delete(API_API_KEY_BY_ID, {}, { id })
}

export const getIntegrationChannels = () => {
  return Api.get(API_INTEGRATION_CHANNELS)
}

export const createIntegrationChannel = (data: Record<string, any>) => {
  return Api.post(API_INTEGRATION_CHANNELS, data)
}

export const updateIntegrationChannel = (id: number, data: Record<string, any>) => {
  return Api.put(API_INTEGRATION_CHANNEL_BY_ID, data, { id })
}

export const deleteIntegrationChannel = (id: number) => {
  return Api.delete(API_INTEGRATION_CHANNEL_BY_ID, {}, { id })
}

export const testIntegrationChannel = (id: number) => {
  return Api.post(API_INTEGRATION_CHANNEL_TEST, {}, { id })
}
